import { useEffect, useState, useCallback } from "react";
import CryptoJS from "crypto-js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { apiService } from "../../utils/apiService";
import LoadingSection from "../LoadingSection";
import ErrorComponent from "../Error";
import { selectAccountData } from "../../redux/main/accountSlice";
import Loader from "../Loader";
import CustomToggleSwitch from "../CustomToggleSwitch";
import {
    getUserDetails,
    selectUserDetailsData,
    selectUserDetailsError,
    selectUserDetailsIsLoading
} from "../../redux/users/userDetailsSlice";
import {
    selectRightModalOldState,
    setGoingToClose,
    setHasToRefresh
} from "../../redux/rightModalSlice";
import { useDispatch, useSelector } from "react-redux";

export default function AddEditUser({ userId }) {
    // Chiamata iniziale per ricevere i dettagli dell'utente
    const accountData = useSelector(selectAccountData);
    const token = localStorage.getItem('installamelo-auth-token') ?? '';
    const [userDataInput, setUserDataInput] = useState({});
    const [userTypes, setUserTypes] = useState([]);
    const [roleTypes, setRoleTypes] = useState([]);
    const [isLoadingUserTypes, setIsLoadingUserTypes] = useState(false);
    const [isLoadingRoleTypes, setIsLoadingRoleTypes] = useState(false);
    const [userTypesError, setUserTypesError] = useState('');
    const [roleTypesError, setRoleTypesError] = useState('');

    
    // Funzione per ottenere i tipi di utente
    const fetchUserTypes = useCallback(async () => {
        if (!accountData?._id) return;
        
        setIsLoadingUserTypes(true);
        setUserTypesError('');
        
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/accounts/getUserTypes?accountId=${accountData._id}`;
            const data = await apiService.get(url, {
                headers: {
                    'auth-token': token
                }
            });
            setUserTypes(data);
        } catch (error) {
            console.error("Errore nel caricamento dei tipi di utente:", error);
            setUserTypesError(error.message || "Errore nel caricamento dei tipi di utente");
        } finally {
            setIsLoadingUserTypes(false);
        }
    }, [accountData, token]);
    
    // Funzione per ottenere i tipi di ruolo
    const fetchRoleTypes = useCallback(async () => {
        if (!accountData?._id) return;
        
        setIsLoadingRoleTypes(true);
        setRoleTypesError('');
        
        try {
            const url = `${import.meta.env.VITE_API_BASE_URL}/accounts/getUserRoleTypes?accountId=${accountData._id}`;
            const data = await apiService.get(url, {
                headers: {
                    'auth-token': token
                }
            });
            setRoleTypes(data);
        } catch (error) {
            console.error("Errore nel caricamento dei tipi di ruolo:", error);
            setRoleTypesError(error.message || "Errore nel caricamento dei tipi di ruolo");
        } finally {
            setIsLoadingRoleTypes(false);
        }
    }, [accountData, token]);
    
    // Inserisco accountId appena caricato da Redux
    useEffect(() => {
        if (accountData) {
            setUserDataInput((prevState) => ({
                ...prevState,
                accountId: accountData._id
            }))
        }
    }, [accountData])
    
    // Carico i tipi di utente e i ruoli quando il componente viene montato
    useEffect(() => {
        if (accountData?._id) {
            fetchUserTypes();
            fetchRoleTypes();
        }
    }, [accountData, fetchUserTypes, fetchRoleTypes]);
    
    // Imposto valori di default per le select quando si crea un nuovo utente
    useEffect(() => {
        // Solo per nuovi utenti (quando userId non è presente)
        if (!userId && userTypes.length > 0 && roleTypes.length > 0) {
            setUserDataInput((prevState) => ({
                // Mantengo solo accountId e resetto tutti gli altri campi
                accountId: prevState.accountId,
                // Pulisco tutti i campi di input
                name: "",
                surname: "",
                email: "",
                mobile: "",
                notes: "",
                password: "",
                repeatPassword: "",
                // Imposto un colore predefinito per l'utente
                color: "#666666",
                // Imposto solo il tipo e ruolo come valori di default
                type: userTypes[0]._id,
                typeId: userTypes[0]._id, // Aggiungo anche typeId
                typeLabel: userTypes[0].label,
                role: roleTypes[0]._id,
                roleId: roleTypes[0]._id, // Aggiungo anche roleId
                roleLabel: roleTypes[0].label,
                // Imposto active = true come default per i nuovi utenti
                active: true
            }));
        }
    }, [userId, userTypes, roleTypes]);
    
    const dispatch = useDispatch();
    const userDetailsData = useSelector(selectUserDetailsData);
    const userDetailsIsLoading = useSelector(selectUserDetailsIsLoading);
    const userDetailsError = useSelector(selectUserDetailsError);
    
    useEffect(() => {
        if (userId) dispatch(getUserDetails({ userId, token }))
    }, [userId, dispatch, token])
    
    // Ricevuti i dati utente da Redux imposto il userDataInput per la modifica
    useEffect(() => {
        if (userDetailsData) {
            // Salvo l'ID del ruolo e del tipo sia in formato role/type che in formato roleId/typeId
            const roleValue = userDetailsData.roleId || userDetailsData.role;
            const typeValue = userDetailsData.typeId || userDetailsData.type;
            
            setUserDataInput((prevState) => ({
                ...prevState,
                ...userDetailsData,
                // Imposto correttamente i valori per le select
                role: roleValue,
                roleId: roleValue, // Assicuro che anche roleId sia valorizzato
                type: typeValue,
                typeId: typeValue  // Assicuro che anche typeId sia valorizzato
            }))
        }
    }, [userDetailsData])

    // Funzione generica per aggiornare il userDataInput
    const handleUserDataInput = (event) => {
        const { id, value } = event.target;
        setUserDataInput((prevState) => ({
            ...prevState,
            [id]: value
        }))
    }
    
    // Funzione per gestire il cambio di tipo utente
    const handleUserTypeChange = (event) => {
        const selectedType = userTypes.find(type => type._id === event.target.value);
        if (selectedType) {
            setUserDataInput((prevState) => ({
                ...prevState,
                // Salvo sia come type che come typeId per compatibilità
                type: selectedType._id,
                typeId: selectedType._id,
                typeLabel: selectedType.label
            }));
        }
    };
    
    // Funzione per gestire il cambio di ruolo
    const handleRoleTypeChange = (event) => {
        const selectedRole = roleTypes.find(role => role._id === event.target.value);
        if (selectedRole) {
            setUserDataInput((prevState) => ({
                ...prevState,
                // Salvo sia come role che come roleId per compatibilità
                role: selectedRole._id,
                roleId: selectedRole._id,
                roleLabel: selectedRole.label
            }));
        }
    };
    
    // Funzione per validare i dati prima dell'invio
    const validateUserData = () => {
        // Verifica che i campi obbligatori siano compilati
        if (!userDataInput.name || !userDataInput.surname || !userDataInput.email) {
            setSendError("Nome, Cognome e Email sono campi obbligatori");
            setSendFetchStatus('f'); // Imposto lo stato a 'f' (failure) per mantenere i bottoni visibili
            return false;
        }
        
        // Verifica che il tipo e il ruolo siano selezionati
        if (!userDataInput.type || !userDataInput.role) {
            setSendError("Tipo e Ruolo sono campi obbligatori");
            setSendFetchStatus('f'); // Imposto lo stato a 'f' (failure) per mantenere i bottoni visibili
            return false;
        }
        

        
        // Verifica che la password sia compilata per i nuovi utenti
        if (!userId && !userDataInput.password) {
            setSendError("La password è obbligatoria per i nuovi utenti");
            setSendFetchStatus('f'); // Imposto lo stato a 'f' (failure) per mantenere i bottoni visibili
            return false;
        }
        
        // Verifica che le password corrispondano per i nuovi utenti
        if (!userId && userDataInput.password !== userDataInput.repeatPassword) {
            setSendError("Le password non corrispondono");
            setSendFetchStatus('f'); // Imposto lo stato a 'f' (failure) per mantenere i bottoni visibili
            return false;
        }
        
        return true;
    };

    const [sendFetchStatus, setSendFetchStatus] = useState('i');
    const [sendError, setSendError] = useState('');
    const [showPasswordSection, setShowPasswordSection] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showRepeatPassword, setShowRepeatPassword] = useState(false);
    const sendUserData = async () => {
        // Validazione dei dati
        if (!validateUserData()) {
            // Non cambiamo sendFetchStatus, mostriamo solo l'errore
            return;
        }
        
        setSendFetchStatus('l');
        const url = `${import.meta.env.VITE_API_BASE_URL}/users`;
        
        try {
            // Creo una copia dei dati dell'utente per poterla modificare
            const userData = { ...userDataInput };
            
            // Se stiamo creando un nuovo utente, devo criptare la password
            if (!userId && userData.password) {
                // Crittografo la password nello stesso modo del cambio password
                userData.password = CryptoJS.AES.encrypt(
                    userData.password,
                    import.meta.env.VITE_ENCKEY
                ).toString();
                
                // Rimuovo la password di conferma che non serve al backend
                delete userData.repeatPassword;
                
                console.log('Password criptata per nuovo utente');
            }
            
            // Assicuro che typeId e roleId siano correttamente impostati
            if (userData.typeId === undefined || userData.typeId === '') {
                userData.typeId = userData.type?.id || '';
            }
            
            if (userData.roleId === undefined || userData.roleId === '') {
                userData.roleId = userData.role?.id || '';
            }
            
            // Assicuro che vengano passati anche i campi label
            if (userData.typeLabel === undefined || userData.typeLabel === '') {
                userData.typeLabel = userData.type?.label || '';
            }
            
            if (userData.roleLabel === undefined || userData.roleLabel === '') {
                userData.roleLabel = userData.role?.label || '';
            }
            
            // Log dettagliato per confermare che il colore è presente nei dati inviati
            console.log('Dati utente inviati:', userData);
            console.log('Colore utente inviato:', userData.color);
            
            // Assicurarsi esplicitamente che color sia definito
            if (!userData.color) {
                userData.color = "#666666"; // Valore predefinito fallback
                console.log('Impostato colore di default:', userData.color);
            }
            
            // Utilizzo fetch direttamente
            const response = await fetch(url, {
                method: userId ? 'PATCH' : 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify(userData)
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Errore da parte del server.");
            }
            
            const resultData = await response.json();
            
            console.log('resultData: ', resultData);
            setSendFetchStatus('s');
            // Resetto eventuali errori precedenti quando l'operazione va a buon fine
            setSendError('');
            dispatch(setHasToRefresh());
            
            // Chiudo la modale dopo 1.5 secondi per mostrare il messaggio di successo
            setTimeout(() => {
                dispatch(setGoingToClose());
            }, 1500);
        } catch (error) {
            console.error(error.message || "Errore da parte del server.");
            setSendFetchStatus('f');
            setSendError(error.message || "Errore da parte del server.");
        }
    }

    // Funzione per il cambio password
    const changePassword = async () => {
        // Verifica che le due password siano uguali
        if (userDataInput.password !== userDataInput.repeatPassword) {
            setSendError("Le due password non coincidono");
            setSendFetchStatus('f'); // Imposto lo stato a 'f' (failure) per mantenere i bottoni visibili
            return;
        }
        
        // Verifica che la password non sia vuota
        if (!userDataInput.password) {
            setSendError("La password non può essere vuota");
            setSendFetchStatus('f');
            return;
        }
        
        // Se le password coincidono, procedi con il salvataggio
        setSendFetchStatus('l');
        setSendError('');
        
        try {
            // Crittografo la password prima di inviarla
            const encryptedPassword = CryptoJS.AES.encrypt(
                userDataInput.password,
                import.meta.env.VITE_ENCKEY
            ).toString();
            
            // Log per debug
            console.log("userId:", userId);
            console.log("encryptedPassword:", encryptedPassword);
            console.log("VITE_ENCKEY:", import.meta.env.VITE_ENCKEY);
            
            const url = `${import.meta.env.VITE_API_BASE_URL}/users/changePassword`;
            
            // Utilizzo fetch direttamente invece di apiService
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'auth-token': token
                },
                body: JSON.stringify({
                    userId: userId,
                    password: encryptedPassword
                })
            });
            
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || "Errore durante il cambio password");
            }
            
            // Se l'operazione va a buon fine
            setSendFetchStatus('s');
            // Resetto eventuali errori precedenti quando l'operazione va a buon fine
            setSendError('');
            
            // Chiudi il box di modifica password dopo 2 secondi
            setTimeout(() => {
                setShowPasswordSection(false);
                // Reset dei campi password
                setUserDataInput(prev => ({
                    ...prev,
                    password: '',
                    repeatPassword: ''
                }));
                setSendFetchStatus('i');
            }, 2000);
            
        } catch (error) {
            console.error(error.message || "Errore durante il cambio password.");
            setSendFetchStatus('f');
            setSendError(error.message || "Errore durante il cambio password.");
        }
    }
    
    // Gestisco il tasto Torna indietro in caso di provenienza da un'altra modale
    const rightModalOldState = useSelector(selectRightModalOldState);
    
    return (
        <div className="w-full p-8">
            {
                userDetailsIsLoading && <LoadingSection isFixed={false} />
            }
            {
                userDetailsError && <ErrorComponent message={userDetailsError} />
            }
            {
                (userDetailsData || !userId) &&
                <div className="flex flex-col gap-4 mt-8 border-2 rounded-lg p-4 border-blue-200 relative">
                    <h1>{userId ? 'Modifica Utente' : 'Nuovo Utente'}</h1>
                    {userId && <div className="light-text mt-[-24px]">Id utente: {userId}</div>}
                    
                    {/* INFORMAZIONI */}
                    <h2>Informazioni</h2>
                    
                    {/* Nome e Cognome */}
                    <div className="flex gap-2">
                        <div className="input-box flex-1">
                            <label htmlFor="name">Nome</label>
                            <input
                                type="text"
                                id="name"
                                value={userDataInput?.name || ""}
                                onChange={handleUserDataInput}
                            />
                        </div>
                        <div className="input-box flex-1">
                            <label htmlFor="surname">Cognome</label>
                            <input
                                type="text"
                                id="surname"
                                value={userDataInput?.surname || ""}
                                onChange={handleUserDataInput}
                            />
                        </div>
                    </div>
                    
                    {/* Email e Mobile */}
                    <div className="flex gap-2">
                        <div className="input-box flex-1">
                            <label htmlFor="email">Email</label>
                            <input
                                type="email"
                                id="email"
                                value={userDataInput?.email || ""}
                                onChange={handleUserDataInput}
                            />
                        </div>
                        <div className="input-box flex-1">
                            <label htmlFor="mobile">Cellulare</label>
                            <input
                                type="text"
                                id="mobile"
                                value={userDataInput?.mobile || ""}
                                onChange={handleUserDataInput}
                            />
                        </div>
                    </div>
                    
                    {/* Selettore Colore */}
                    <div className="flex gap-2">
                        <div className="input-box flex-1">
                            <label htmlFor="color">Colore utente</label>
                            <div className="flex items-center gap-2">
                                <input
                                    type="color"
                                    id="colorPicker"
                                    className="w-12 h-8 cursor-pointer"
                                    value={userDataInput?.color || "#666666"}
                                    onChange={(e) => {
                                        // Aggiorniamo direttamente il colore senza usare handleUserDataInput
                                        const newColor = e.target.value;
                                        console.log('Nuovo colore selezionato:', newColor);
                                        setUserDataInput((prevState) => ({
                                            ...prevState,
                                            color: newColor
                                        }));
                                    }}
                                />
                                <input
                                    type="text"
                                    id="colorText"
                                    className="flex-grow"
                                    value={userDataInput?.color || "#666666"}
                                    onChange={(e) => {
                                        // Aggiorniamo direttamente il colore dal campo di testo
                                        const newColor = e.target.value;
                                        console.log('Nuovo colore inserito manualmente:', newColor);
                                        setUserDataInput((prevState) => ({
                                            ...prevState,
                                            color: newColor
                                        }));
                                    }}
                                    placeholder="Codice HEX (es. #FF5733)"
                                />
                                <div
                                    className="w-8 h-8 rounded-full border border-gray-300"
                                    style={{ backgroundColor: userDataInput?.color || "#666666" }}
                                ></div>
                            </div>
                            <div className="text-xs text-gray-500 mt-1">Il colore sarà utilizzato per identificare l'utente nel calendario e in altre sezioni</div>
                        </div>
                    </div>
                    
                    {/* Ruolo e Tipo */}
                    <div className="flex gap-2">
                        <div className="input-box flex-1">
                            <label htmlFor="role">Ruolo <span className="text-red-500">*</span></label>
                            {isLoadingRoleTypes ? (
                                <div className="flex items-center justify-center h-[38px]">
                                    <Loader />
                                </div>
                            ) : roleTypesError ? (
                                <ErrorComponent message={roleTypesError} />
                            ) : (
                                <select
                                    id="role"
                                    value={userDataInput?.role || ""}
                                    onChange={handleRoleTypeChange}
                                    className="w-full p-2 border rounded"
                                    required
                                >
                                    <option value="">Seleziona un ruolo</option>
                                    {roleTypes.map(role => (
                                        <option key={role._id} value={role._id}>
                                            {role.label}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                        <div className="input-box flex-1">
                            <label htmlFor="type">Tipo <span className="text-red-500">*</span></label>
                            {isLoadingUserTypes ? (
                                <div className="flex items-center justify-center h-[38px]">
                                    <Loader />
                                </div>
                            ) : userTypesError ? (
                                <ErrorComponent message={userTypesError} />
                            ) : (
                                <select
                                    id="type"
                                    value={userDataInput?.type || ""}
                                    onChange={handleUserTypeChange}
                                    className="w-full p-2 border rounded"
                                    required
                                >
                                    <option value="">Seleziona un tipo</option>
                                    {userTypes.map(type => (
                                        <option key={type._id} value={type._id}>
                                            {type.label}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </div>
                    
                    {/* Note */}
                    <div className="input-box">
                        <label htmlFor="notes">Note</label>
                        <textarea
                            id="notes"
                            value={userDataInput?.notes || ""}
                            onChange={handleUserDataInput}
                            rows={4}
                        />
                    </div>
                    
                    {/* Configurazione Menu Mobile (solo per utenti non customer) */}
                    <div className="mt-4 p-4 border rounded-lg bg-gray-50">
                        <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                            <span>📱</span> Voci Menu Mobile
                        </h3>
                        <p className="text-sm text-gray-600 mb-4">
                            Seleziona quali voci mostrare nella bottom bar dell'app mobile per questo utente.
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                            {[
                                { id: 'home', icon: 'home', label: 'Home', route: '/home' },
                                { id: 'calendar', icon: 'calendar_month', label: 'Calendario', route: '/calendar' },
                                { id: 'customers', icon: 'contacts', label: 'Clienti', route: '/customers' },
                                { id: 'reports', icon: 'assessment', label: 'Report', route: '/reports' },
                                { id: 'quotes', icon: 'request_quote', label: 'Preventivi', route: '/quotes' },
                                { id: 'warehouse', icon: 'inventory', label: 'Magazzino', route: '/warehouse' }
                            ].map((item, index) => {
                                const isSelected = userDataInput?.mobileMenuItems?.some(m => m.id === item.id) || false;
                                return (
                                    <label 
                                        key={item.id} 
                                        className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                                            isSelected 
                                                ? 'bg-blue-100 border-blue-500 text-blue-700' 
                                                : 'bg-white border-gray-200 hover:border-gray-300'
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={(e) => {
                                                const currentItems = userDataInput?.mobileMenuItems || [];
                                                if (e.target.checked) {
                                                    // Aggiungi voce
                                                    setUserDataInput(prev => ({
                                                        ...prev,
                                                        mobileMenuItems: [
                                                            ...currentItems,
                                                            { ...item, order: currentItems.length }
                                                        ]
                                                    }));
                                                } else {
                                                    // Rimuovi voce
                                                    setUserDataInput(prev => ({
                                                        ...prev,
                                                        mobileMenuItems: currentItems.filter(m => m.id !== item.id)
                                                    }));
                                                }
                                            }}
                                            className="w-4 h-4"
                                        />
                                        <span className="font-medium">{item.label}</span>
                                    </label>
                                );
                            })}
                        </div>
                        {userDataInput?.mobileMenuItems?.length > 0 && (
                            <div className="mt-3 text-sm text-gray-500">
                                Voci selezionate: {userDataInput.mobileMenuItems.map(m => m.label).join(', ')}
                            </div>
                        )}
                    </div>
                    
                    {/* Pulsante Modifica Password (solo per utenti esistenti) */}
                    {userId && (
                        <div>
                            <button 
                                type="button" 
                                className="text-gray-500 text-sm px-3 py-1 rounded border border-gray-300 hover:bg-gray-100"
                                onClick={() => setShowPasswordSection(!showPasswordSection)}
                            >
                                Modifica Password
                            </button>
                            
                            {/* Sezione modifica password */}
                            {showPasswordSection && (
                                <div className="mt-3">
                                    <div className="flex gap-2">
                                        <div className="input-box flex-1 relative">
                                            <label htmlFor="password">Nuova Password</label>
                                            <div className="relative">
                                                <input
                                                    type={showPassword ? "text" : "password"}
                                                    id="password"
                                                    value={userDataInput?.password || ""}
                                                    onChange={handleUserDataInput}
                                                    className="w-full pr-10"
                                                />
                                                <button 
                                                    type="button"
                                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500"
                                                    onClick={() => setShowPassword(!showPassword)}
                                                >
                                                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="input-box flex-1 relative">
                                            <label htmlFor="repeatPassword">Ripeti Password</label>
                                            <div className="relative">
                                                <input
                                                    type={showRepeatPassword ? "text" : "password"}
                                                    id="repeatPassword"
                                                    value={userDataInput?.repeatPassword || ""}
                                                    onChange={handleUserDataInput}
                                                    className="w-full pr-10"
                                                />
                                                <button 
                                                    type="button"
                                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500"
                                                    onClick={() => setShowRepeatPassword(!showRepeatPassword)}
                                                >
                                                    <FontAwesomeIcon icon={showRepeatPassword ? faEyeSlash : faEye} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                    {sendError && (
                                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-2">
                                            {sendError}
                                        </div>
                                    )}
                                    {sendFetchStatus === 's' && (
                                        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mt-2">
                                            Password completata correttamente
                                        </div>
                                    )}
                                    <div className="flex justify-end mt-2">
                                        <button 
                                            type="button" 
                                            className="text-sm px-3 py-1 rounded bg-blue-500 text-white hover:bg-blue-600"
                                            onClick={changePassword}
                                            disabled={sendFetchStatus === 'l' || sendFetchStatus === 's'}
                                        >
                                            {sendFetchStatus === 'l' ? 'Caricamento...' : 'Cambia Password'}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    

                    
                    {/* Password e Ripeti Password (solo per nuovi utenti) */}
                    {!userId && (
                        <div className="flex gap-2">
                            <div className="input-box flex-1">
                                <label htmlFor="password">Password</label>
                                <input
                                    type="password"
                                    id="password"
                                    value={userDataInput?.password || ""}
                                    onChange={handleUserDataInput}
                                />
                            </div>
                            <div className="input-box flex-1">
                                <label htmlFor="repeatPassword">Ripeti Password</label>
                                <input
                                    type="password"
                                    id="repeatPassword"
                                    value={userDataInput?.repeatPassword || ""}
                                    onChange={handleUserDataInput}
                                />
                            </div>
                        </div>
                    )}
                    
                    {/* Messaggio di errore con sfondo rosso trasparente */}
                    {sendError && (
                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                            {sendError}
                        </div>
                    )}
                    
                    
                    <div className="flex gap-2 h-[40px]">
                        {/* Mostra i bottoni sempre tranne quando è in caricamento o successo senza errori */}
                        {
                            (sendFetchStatus !== 'l' && sendFetchStatus !== 's') || sendError ?
                            <div className="flex gap-2">
                                <button className="primary" onClick={sendUserData}>{userId ? 'Salva Utente' : 'Crea Nuovo Utente'}</button>
                                <button className="secondary" onClick={() => dispatch(setGoingToClose())}>Annulla</button>
                            </div>
                            : null
                        }
                        {
                            sendFetchStatus === 'l' && !sendError &&
                            <Loader />
                        }
                        {
                            sendFetchStatus === 's' && !sendError &&
                            <h3>Utente {userId ? 'aggiornato' : 'creato'} con successo.</h3>
                        }
                    </div>
                </div>
            }
        </div>
    )
}
